<template>
  <div
    ref="rootRef"
    class="umv"
    :class="{ 'umv--mobile': isMobile, 'is-dragging': isDragging }"
    @click.capture="onClickCapture"
  >
    <!-- 요일 헤더 -->
    <div class="umv__weekdays">
      <div
        v-for="(d, i) in WEEK_DAYS"
        :key="d"
        class="umv__weekday"
        :class="{ 'is-sun': i === 0, 'is-sat': i === 6 }"
      >{{ d }}</div>
    </div>

    <!-- 주 -->
    <div
      v-for="(week, wi) in packedWeeks"
      :key="wi"
      class="umv__week"
      :data-week="wi"
      :style="weekStyle(week, wi)"
    >
      <!-- 날짜 칸 (모바일에서는 칸 전체가 탭 대상) -->
      <button
        v-for="(day, di) in week.days"
        :key="di"
        type="button"
        class="umv__day"
        :class="dayClass(day)"
        :style="{ gridColumn: `${di + 1} / ${di + 2}` }"
        :data-date="ymd(day)"
        :aria-label="dayAriaLabel(day, week)"
        @click="onDayClick(day)"
      >
        <span class="umv__daynum">{{ day.getDate() }}</span>
        <span v-if="holidayName(ymd(day))" class="umv__holiday">{{ holidayName(ymd(day)) }}</span>
      </button>

      <!-- 스팬 (여러 날 / 종일) -->
      <div
        v-for="s in visibleSpans(week)"
        :key="s.item.uid"
        class="umv__chip"
        :class="chipClass(s.item, s)"
        :style="chipStyle(s.item, spanStyle(s))"
        :title="chipTitle(s.item)"
        @pointerdown="onChipPointerDown($event, s.item)"
        @dragstart.prevent
        @click.stop="onItemClick(s.item)"
      >
        <span
          v-if="s.item.kind === 'task'"
          class="umv__check"
          :class="{ 'is-done': s.item.status === 'done' }"
          aria-hidden="true"
        />
        <span class="umv__label">{{ s.item.title }}</span>
        <!--
          주가 바뀌어 이어진 조각(continuesLeft)에도 **누구 것인지 붙인다.**
          예전에는 `!s.continuesLeft` 로 막아 뒀는데, 한 줄짜리 막대에서 같은
          아바타가 두 번 나오는 걸 피하려던 것이었다. 하지만 이어진 조각은
          **다음 주 행**에 있어서 앞 조각과 나란히 보이는 일이 없다 —
          화요일에 시작해 다음 주 목요일에 끝나는 외근이 있으면, 둘째 주 행에는
          제목만 있고 누구 것인지가 통째로 사라졌다.
          (제목은 이어진 조각에도 그대로 다시 찍히고 있었으니 기준도 어긋나 있었다)
        -->
        <span v-if="s.item.owner" class="umv__who">
          <Avatar :user="s.item.owner" />
          <span v-if="showName(s)" class="umv__nick">{{ s.item.owner.nickname }}</span>
        </span>
      </div>

      <!-- 포인트 (단일일 시간 항목) -->
      <template v-for="(col, ci) in week.points" :key="`p${ci}`">
        <div
          v-for="(item, pi) in visiblePoints(week, col)"
          :key="item.uid"
          class="umv__chip"
          :class="chipClass(item)"
          :style="chipStyle(item, pointStyle(week, ci, pi))"
          :title="chipTitle(item)"
          @pointerdown="onChipPointerDown($event, item)"
          @dragstart.prevent
          @click.stop="onItemClick(item)"
        >
          <span v-if="!isMobile" class="umv__time">{{ timeLabel(item) }}</span>
          <span class="umv__label">{{ item.title }}</span>
          <span v-if="item.owner" class="umv__who">
            <Avatar :user="item.owner" />
            <span v-if="showName()" class="umv__nick">{{ item.owner.nickname }}</span>
          </span>
        </div>
      </template>

      <!-- 넘침 -->
      <button
        v-for="o in overflows(week)"
        :key="`o${o.col}`"
        type="button"
        class="umv__more"
        :style="{ gridColumn: `${o.col + 1} / ${o.col + 2}`, gridRow: laneRow(maxLanes) }"
        @click.stop="$emit('overflow-click', ymd(week.days[o.col]))"
      >+{{ o.count }}</button>
    </div>

    <!-- 끌고 있는 동안 커서 옆에 붙는 착지 날짜 -->
    <div v-if="dropLabel" ref="pillRef" class="umv__droppill" aria-hidden="true">{{ dropLabel }}</div>
  </div>
</template>

<script>
import { computed, ref, toRef, watchEffect, h } from 'vue'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useHolidays } from '@/composables/useHolidays'
import { useMonthDragMove } from '@/composables/useMonthDragMove'
import { buildMonthWeeks, useMonthGrid, WEEK_DAYS } from '@/composables/useMonthGrid'
import { ymd, timeLabel } from '@/utils/agendaItem'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'

/**
 * 아주 작은 아바타. el-avatar 는 이 크기에서 테두리/그림자가 과해서 직접 그린다.
 */
const Avatar = {
  name: 'MiniAvatar',
  props: { user: { type: Object, required: true } },
  setup(props) {
    return () => {
      const src = getProfileImageUrl(props.user)
      return src
        // draggable=false 가 없으면 아바타 위에서 끌 때 브라우저의 이미지
        // 드래그가 먼저 발동해 칩 이동이 중간에 끊긴다
        ? h('img', { class: 'mini-av', src, alt: props.user.nickname, loading: 'lazy', draggable: false })
        : h('span', { class: 'mini-av mini-av--txt' }, getProfileInitial(props.user))
    }
  }
}

/**
 * 일정 + 업무를 함께 렌더하는 월 그리드.
 *
 * ── 읽히는 캘린더를 만드는 두 가지 ──
 *
 * 1) 색은 **유형**이다.
 *    휴가/휴무/외근/점검/대직/업무가 각자 고정된 색을 갖는다. 이 팀이 캘린더에
 *    던지는 첫 질문이 "이번 달에 누가 언제 빠지나" 라서, 유형이 색으로 구분되지
 *    않으면 캘린더가 아니라 목록이 된다. 칩 전체를 그 색으로 칠한다.
 *    (서버가 events.color 컬럼 대신 eventType 에서 색을 파생한다 — 그 컬럼에는
 *     팔레트 두 세대가 섞여 있어 같은 유형이 두 색으로 저장돼 있다)
 *
 * 2) 누구 일정인지가 칩 안에 있어야 한다.
 *    프로필 이미지를 칩 오른쪽에 붙인다. 모바일에서도 유지한다 — 텍스트를
 *    다 지우더라도 색과 얼굴은 남겨야 한 눈에 읽힌다.
 *
 * 업무는 일정과 구분되어야 하므로 채우기 대신 옅은 배경 + 좌측 색 띠 + 체크박스로
 * 그린다. 같은 그리드 안에서 "일정인가 업무인가" 가 형태로 즉시 구분된다.
 */
export default {
  name: 'UnifiedMonthView',
  components: { Avatar },
  props: {
    items: { type: Array, default: () => [] },
    date: { type: Date, required: true },
    interactive: { type: Boolean, default: true }
  },
  emits: ['day-click', 'item-click', 'overflow-click', 'item-move'],
  setup(props, { emit }) {
    const { isMobile } = useBreakpoint()
    const monthDate = toRef(props, 'date')
    const items = toRef(props, 'items')

    const rootRef = ref(null)

    /*
     * 주(週) 배열은 항목과 무관하게 월만으로 정해진다. 드래그 엔진이 좌표를
     * 날짜로 되돌릴 때 이게 필요한데 useMonthGrid 가 주는 것을 쓰면 순환이
     * 된다 — 그리드는 드래그의 미리보기를 먹고, 드래그는 그리드의 주를 먹는다.
     * 42개 Date 를 만드는 계산이라 여기서 따로 만든다.
     */
    const weeks = computed(() => buildMonthWeeks(monthDate.value))

    const hasFinePointer =
      typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches
    const dragEnabled = computed(() => props.interactive && hasFinePointer && !isMobile.value)

    const drag = useMonthDragMove({
      rootRef,
      weeks,
      items,
      enabled: dragEnabled,
      onMove: (payload) => emit('item-move', payload)
    })
    const { isDragging, dropLabel, onClickCapture } = drag

    /*
     * 착지 날짜 알약의 위치는 **DOM 에 직접 쓴다.**
     * 템플릿에서 pointerX/pointerY 를 읽으면 마우스가 1px 움직일 때마다
     * 그리드 전체(칩 수백 개)가 다시 렌더된다. 드래그는 부드러움이 전부인
     * 조작이라 그 비용을 낼 이유가 없다 — 다시 그려야 하는 건 착지 날짜가
     * 바뀔 때뿐이고, 그건 dropLabel/deltaDays 가 이미 처리한다.
     * watchEffect 는 렌더와 별개로 돌기 때문에 여기서 좌표를 읽어도
     * 컴포넌트의 렌더 의존성에 들어가지 않는다.
     */
    const pillRef = ref(null)
    watchEffect(
      () => {
        const el = pillRef.value
        if (!el) return
        const x = drag.pointerX.value
        const y = drag.pointerY.value
        // 화면 오른쪽/아래 끝에서는 커서 반대쪽으로 뒤집는다. position:fixed 라
        // 넘치면 스크롤이 생기는 게 아니라 그냥 잘려서 읽을 수가 없다.
        const flipX = x > window.innerWidth - 180
        const flipY = y > window.innerHeight - 56
        el.style.transform =
          `translate(${x}px, ${y}px) ` +
          `translate(${flipX ? 'calc(-100% - 14px)' : '14px'}, ${flipY ? 'calc(-100% - 12px)' : '16px'})`
      },
      { flush: 'post' }
    )

    // 드래그 직전의 주별 레인 수. weekStyle 이 높이를 잠그는 데 쓴다.
    const frozenLanes = ref(null)

    /*
     * 드래그 중에는 **옮겨진 사본**을 그린다. 패킹이 다시 돌면서 옮겨진 칩이
     * 최종 레인에 앉고 다른 칩들이 실제로 비켜 준다 — 화면이 곧 결과다.
     * 제자리일 때는 원본 배열을 그대로 돌려주므로 패킹이 다시 돌지 않는다.
     */
    const gridItems = computed(() => drag.previewItems.value || items.value)

    const { packedWeeks } = useMonthGrid(monthDate, gridItems)

    // 6주 그리드는 앞뒤 달을 걸친다. 1월이면 12월 말(성탄절), 12월이면 1월 1일이 보인다.
    const holidayYears = computed(() => {
      const w = weeks.value
      return [w[0][0].getFullYear(), w[5][6].getFullYear()]
    })
    const { holidayName } = useHolidays(holidayYears)

    /**
     * 레인 상한.
     * 데스크톱은 내용에 맞춰 늘린다 — 하루 5건인데 4건만 보이면 캘린더로 못 쓴다.
     * 모바일은 6주 그리드가 세로로 감당이 안 되므로 4레인에서 끊고 +N 으로 넘긴다
     * (+N 을 누르면 그 날의 전체 목록이 시트로 열린다).
     */
    const MOBILE_MAX_LANES = 4
    const DESKTOP_HARD_CAP = 20 // 데이터 이상 시 폭주 방지용 안전장치

    /*
     * 필요한 줄 수는 **렌더가 실제로 쓰는 식과 같아야 한다.**
     *
     * 포인트는 칸별로 쌓이는 게 아니라 그 주의 스팬 레인을 **전부 지난 뒤**부터
     * 쌓인다(pointBaseLane 참고). 그래서 필요한 줄은 `laneCount + 그 주에서
     * 가장 붐비는 컬럼의 포인트 수` 다.
     *
     * 예전 식은 `그 컬럼을 덮는 스팬 수 + 그 컬럼의 포인트 수` 였다. 스팬과
     * 포인트가 서로 다른 요일에 있으면 이게 실제 필요량보다 작게 나온다.
     * 그 결과 두 가지가 동시에 벌어졌다:
     *   1. 데스크톱인데도 그 주의 시간 항목이 통째로 +N 뒤로 사라진다
     *      ("데스크톱은 내용에 맞춰 늘린다" 는 이 계산의 목적과 정반대다).
     *      월·화에 겹친 휴가 2건 + 금요일 회의 1건이면 이미 재현된다.
     *   2. 잘린 주의 높이가 자기 내용이 아니라 전역 maxLanes 에 매달린다.
     *      maxLanes 는 packedWeeks → 드래그 미리보기에서 파생되므로
     *      (미리보기 → maxLanes → 남의 주 높이 → 커서 밑의 칸 → 미리보기)
     *      되먹임 고리가 닫히고, 커서를 조금만 떨어도 착지 날짜가 7일씩 튄다.
     *
     * 식을 맞추면 데스크톱에서 maxLanes 가 모든 주의 필요량 이상이 되어
     * 어떤 주도 잘리지 않는다 → 높이가 maxLanes 와 무관해지고 고리도 끊긴다.
     */
    const neededLanes = computed(() => {
      let need = 1
      for (const week of packedWeeks.value) {
        need = Math.max(need, week.laneCount + Math.max(0, ...week.points.map((p) => p.length)))
      }
      return Math.min(need, DESKTOP_HARD_CAP)
    })

    const maxLanes = computed(() => (isMobile.value ? MOBILE_MAX_LANES : neededLanes.value))

    function laneRow(lane) { return `${lane + 2} / ${lane + 3}` }
    function visibleSpans(week) { return week.spans.filter((s) => s.lane < maxLanes.value) }
    function pointBaseLane(week) { return Math.min(week.laneCount, maxLanes.value) }

    function visiblePoints(week, col) {
      const room = maxLanes.value - pointBaseLane(week)
      return room > 0 ? col.slice(0, room) : []
    }

    function pointStyle(week, col, idx) {
      return { gridColumn: `${col + 1} / ${col + 2}`, gridRow: laneRow(pointBaseLane(week) + idx) }
    }

    function spanStyle(s) {
      return { gridColumn: `${s.colStart + 1} / ${s.colEnd + 2}`, gridRow: laneRow(s.lane) }
    }

    /** 일정은 유형색으로 채우고, 업무는 상태색 띠 + 옅은 배경 */
    function chipStyle(item, gridStyle) {
      if (item.kind === 'schedule') {
        return { ...gridStyle, background: item.color, borderLeftColor: item.color }
      }
      return { ...gridStyle, borderLeftColor: item.color }
    }

    function chipClass(item, span) {
      const moving = drag.dragUid.value === item.uid
      return {
        'is-draggable': drag.canDrag(item),
        'is-moving': moving,
        'is-outside': moving && drag.outside.value,
        'umv__chip--schedule': item.kind === 'schedule',
        'umv__chip--task': item.kind === 'task',
        'is-done': item.kind === 'task' && item.status === 'done',
        'is-overdue': item.kind === 'task' && item.isOverdue,
        'cont-left': span?.continuesLeft,
        'cont-right': span?.continuesRight
      }
    }

    function chipTitle(item) {
      const who = item.owner ? ` · ${item.owner.nickname}` : ''
      const type = item.kind === 'schedule' ? item.typeLabel : item.statusLabel
      const time = item.allDay ? '' : ` ${timeLabel(item)}`
      return `${item.title}${who} (${type})${time}`
    }

    function overflows(week) {
      const out = []
      week.days.forEach((day, col) => {
        const spansHere = week.spans.filter((s) => s.colStart <= col && s.colEnd >= col)
        const hiddenSpans = spansHere.filter((s) => s.lane >= maxLanes.value).length
        const hiddenPoints = Math.max(
          0,
          week.points[col].length - visiblePoints(week, week.points[col]).length
        )
        const count = hiddenSpans + hiddenPoints
        if (count > 0) out.push({ col, count })
      })
      return out
    }

    /** 이 주가 실제로 차지하는 레인 수 */
    function usedLanes(week) {
      return Math.max(
        1,
        Math.min(
          maxLanes.value,
          week.laneCount + Math.max(0, ...week.points.map((p) => p.length))
        )
      )
    }

    /**
     * 주 행의 그리드 트랙을 **명시**한다.
     *
     * 예전에는 grid-template-rows 없이 minHeight 만 줬다. 그러면 두 가지가 어긋난다:
     *  1. `.umv__day` 의 `grid-row: 1 / -1` 에서 -1 은 **명시 그리드의 마지막 선**을
     *     가리키는데, 명시 트랙이 하나도 없으면 확장이 되지 않는다.
     *  2. 칩이 없는 레인 행은 min-content 라 높이가 0 이어서, minHeight 로 늘어난
     *     아래쪽 공간이 **어떤 행에도 속하지 않는다.**
     * 결과적으로 날짜 버튼이 칸 아래쪽까지 닿지 못해, 빈 곳을 눌러 그 날짜의
     * 일정을 만들던 동작이 하단에서만 먹통이었다.
     *
     * 마지막 1fr 이 남는 공간을 흡수해 행에 속하게 만들고, 그래야 1 / -1 이
     * 칸 전체를 덮는다.
     */
    function weekStyle(week, wi) {
      /*
       * 드래그 중에는 주 높이가 **줄지 않게** 잠근다.
       * 끌기 시작하면 항목이 원래 주에서 빠지면서 그 주가 한 줄 낮아지고,
       * 아래 주들이 통째로 위로 올라온다. 그 순간 커서 밑의 칸이 바뀌어
       * 플레이스홀더가 손끝에서 한 줄(=7일) 어긋난다.
       * 늘어나는 쪽은 막지 않는다 — 늘어남은 아래로만 밀기 때문에
       * 커서가 있는 칸을 건드리지 않는다.
       */
      const floor = isDragging.value ? frozenLanes.value?.[wi] || 0 : 0
      const used = Math.max(usedLanes(week), floor)
      const laneH = isMobile.value ? 18 : 24
      // +N 칩은 laneRow(maxLanes) 에 놓인다. 그 행이 1fr 에 먹혀 납작해지지 않도록
      // 넘침이 있을 때만 전용 트랙을 하나 더 준다.
      const moreRow = overflows(week).length ? ` ${laneH}px` : ''
      return {
        gridTemplateRows:
          `minmax(var(--cal-daynum-h), auto) repeat(${used}, ${laneH}px)${moreRow} 1fr`,
        minHeight: `calc(var(--cal-daynum-h) + ${used * laneH}px + 6px)`
      }
    }

    function dayClass(day) {
      const key = ymd(day)
      return {
        // 렌더마다 잰다. keep-alive 캘린더를 켜 둔 채 자정을 넘기면 상수로는 어제가 남는다.
        'is-today': key === ymd(new Date()),
        'is-other-month': day.getMonth() !== props.date.getMonth(),
        'is-sun': day.getDay() === 0,
        'is-sat': day.getDay() === 6,
        'is-holiday': !!holidayName(key),
        // 스팬이면 덮게 될 칸 전체가 칠해진다 — 며칠짜리가 어디부터 어디까지
        // 놓이는지는 칩 하나만 봐서는 알기 어렵다
        'is-drop-target': !!drag.targetDates.value?.has(key)
      }
    }

    function dayAriaLabel(day, week) {
      const col = week.days.indexOf(day)
      const n =
        week.spans.filter((s) => s.colStart <= col && s.colEnd >= col).length +
        week.points[col].length
      const h = holidayName(ymd(day))
      return `${day.getMonth() + 1}월 ${day.getDate()}일${h ? ' ' + h : ''}${n ? `, 일정 ${n}건` : ''}`
    }

    /**
     * 이름 표시 여부.
     * 데스크톱은 항상 보여준다 — 누구 일정인지가 이 캘린더의 핵심 정보다.
     * 모바일은 한 칸이 약 55px 이라 단일일 칩에 이름까지 넣으면 제목이 사라진다.
     * 2칸 이상 걸친 스팬(휴가/외근 등 실제로 이름이 궁금한 것들)에만 붙인다.
     */
    function showName(span) {
      if (!isMobile.value) return true
      if (!span) return false
      return span.colEnd - span.colStart >= 1
    }

    /*
     * 드래그 직전의 주별 높이를 여기서 기억한다(왜 필요한지는 weekStyle 참고).
     * 이 시점은 아직 미리보기가 붙기 전이라 packedWeeks 가 원본 배치다.
     * 끌지 않고 그냥 클릭했을 때도 값이 남지만, 드래그 중일 때만 읽으므로
     * 다음 pointerdown 이 덮어쓰기 전까지 아무 영향이 없다.
     */
    function onChipPointerDown(e, item) {
      frozenLanes.value = packedWeeks.value.map(usedLanes)
      drag.onChipPointerDown(e, item)
    }

    function onDayClick(day) { emit('day-click', ymd(day)) }

    function onItemClick(item) {
      // 모바일은 칸 전체가 탭 대상이라 칩 개별 클릭을 받지 않는다
      if (isMobile.value || !props.interactive) return
      emit('item-click', item)
    }

    return {
      WEEK_DAYS, packedWeeks, isMobile, maxLanes,
      ymd, timeLabel, holidayName,
      laneRow, visibleSpans, visiblePoints, pointStyle, spanStyle,
      chipStyle, chipClass, chipTitle, overflows, showName,
      weekStyle, dayClass, dayAriaLabel, onDayClick, onItemClick,
      // 드래그 이동
      rootRef, pillRef, isDragging, dropLabel,
      onChipPointerDown, onClickCapture
    }
  }
}
</script>

<style scoped>
.umv {
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
}

.umv__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid var(--color-border);
}

.umv__weekday {
  padding: 8px 0;
  text-align: center;
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.04em;
  color: var(--color-text-muted);
}
/* 주말 색은 tokens.css 의 --cal-sun/--cal-sat 이다. 좌측 레일의 미니 캘린더와
   같은 규칙을 따라야 해서 토큰으로 올렸다(그쪽은 잉크 배경용 쌍을 쓴다). */
.umv__weekday.is-sun { color: var(--cal-sun); }
.umv__weekday.is-sat { color: var(--cal-sat); }

.umv__week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: min-content;
  border-bottom: 1px solid var(--color-border);
  position: relative;
}

.umv__day {
  /*
   * 컬럼은 **템플릿에서 명시**한다(:style 의 gridColumn).
   * 자동 배치로 두면 안 된다 — 이 버튼이 1행이 아니라 칸 전체(1 / -1)를
   * 덮게 되면서 이미 배치된 칩과 셀이 겹치고, 자동 배치 알고리즘이
   * 날짜 버튼을 빈 컬럼으로 밀어낸다. 그러면 일정이 엉뚱한 날짜에
   * 붙어 보인다. 명시 배치된 그리드 아이템끼리는 겹쳐도 된다.
   */
  grid-row: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  padding: 3px 3px 0;
  border: 0;
  border-right: 1px solid var(--color-border);
  background: none;
  cursor: pointer;
  min-width: 0;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.umv__day:nth-child(7n) { border-right: 0; }
.umv__day:active { background: var(--color-surface-2); }
.umv__day.is-other-month { background: var(--color-surface-3); }

.umv__daynum {
  height: var(--cal-daynum-h);
  min-width: var(--cal-daynum-h);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  border-radius: var(--r-full);
}
.is-sun .umv__daynum, .is-holiday .umv__daynum { color: var(--color-danger); }
.is-sat .umv__daynum { color: var(--color-primary); }
.is-other-month .umv__daynum { color: var(--color-text-subtle); }
.is-today .umv__daynum { background: var(--color-primary); color: var(--color-on-accent); }

/* 오늘이 속한 칸을 아주 옅게 깔아 둔다. 날짜 원(파란 채움)만으로는
   6주 그리드에서 눈이 오늘을 찾는 데 시간이 걸린다.
   .is-other-month 와 특이도가 같으므로 뒤에 와야 이긴다. */
.umv__day.is-today { background: var(--cal-today-bg); }

/* 공휴일 이름은 터치 기기에서도 유지한다 — 대직/휴무 계획을 좌우하는 정보다 */
.umv__holiday {
  font-size: var(--fs-3xs);
  line-height: 1.1;
  color: var(--color-danger);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---------- 칩 ---------- */
.umv__chip {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  margin: 0 2px var(--cal-lane-gap);
  padding: 0 3px;
  border-radius: var(--r-xs);
  overflow: hidden;
  cursor: pointer;
  font-size: var(--fs-xs);
  line-height: 1;
}

/* 일정: 유형색으로 꽉 채운다 → 한 눈에 유형이 읽힌다 */
.umv__chip--schedule { color: var(--color-on-accent); }
.umv__chip--schedule .umv__time { color: var(--color-on-accent-dim); }

/* 업무: 옅은 배경 + 좌측 상태색 띠 + 체크박스 → 일정과 형태가 다르다 */
.umv__chip--task {
  background: var(--color-surface-2);
  color: var(--color-text);
  border-left: 3px solid transparent;
  padding-left: 3px;
}
.umv__chip--task.is-done { opacity: 0.55; }
.umv__chip--task.is-done .umv__label { text-decoration: line-through; }
.umv__chip--task.is-overdue { box-shadow: inset 0 0 0 1px var(--color-danger); }

/* 주 경계를 넘어가는 스팬은 그쪽 모서리를 붙인다 */
.cont-left { margin-left: 0; border-top-left-radius: 0; border-bottom-left-radius: 0; }
.cont-right { margin-right: 0; border-top-right-radius: 0; border-bottom-right-radius: 0; }

.umv__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.umv__time { font-variant-numeric: tabular-nums; flex-shrink: 0; }
/* 그려서 만드는 체크박스. 글리프(☑/☐)는 OS·폰트마다 크기가 달라
   11px 칩 안에서 제목의 기준선이 항목마다 흔들렸다. */
.umv__check {
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border: 1.4px solid currentColor;
  border-radius: var(--r-xs);
  opacity: 0.75;
}
.umv__check.is-done { background: currentColor; opacity: 0.9; }

.umv__who {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  max-width: 45%;
}

.umv__nick {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--fw-medium);
}

/* 미니 아바타 (스코프 밖 컴포넌트라 :deep 필요) */
.umv__chip :deep(.mini-av) {
  display: block;
  width: 13px;
  height: 13px;
  border-radius: var(--r-full);
  object-fit: cover;
  background: var(--color-on-accent-veil);
}

.umv__chip--task :deep(.mini-av) { background: var(--color-border); }

.umv__chip :deep(.mini-av--txt) {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-3xs);
  font-weight: var(--fw-semibold);
  color: var(--color-on-accent);
  line-height: 1;
}
.umv__chip--task :deep(.mini-av--txt) { color: var(--color-text-muted); }

.umv__more {
  justify-self: end;
  align-self: center;
  margin-right: 3px;
  border: 0;
  background: none;
  color: var(--color-text-subtle);
  font-size: var(--fs-3xs);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  z-index: 2;
  padding: 0;
}

/* ---------- 모바일 ---------- */
/*
 * 칩 높이를 16px 로 유지한다. 6px 막대로 줄이면 색은 남지만 "누구 일정인지" 와
 * 제목이 사라져서 캘린더가 읽히지 않는다. 3레인 × 18px + 날짜줄이면
 * 주당 약 78px, 6주 468px 로 아이폰 SE(667px)에서도 들어간다.
 */
.umv--mobile .umv__chip {
  height: 16px;
  font-size: var(--fs-2xs);
  gap: 2px;
  /* 칸 전체가 탭 대상이므로 칩은 이벤트를 받지 않는다 */
  pointer-events: none;
}

.umv--mobile .umv__chip :deep(.mini-av) { width: 12px; height: 12px; }
.umv--mobile .umv__nick { font-size: var(--fs-3xs); max-width: 52px; }

/* ---------- 데스크톱 ---------- */
.umv:not(.umv--mobile) .umv__chip {
  height: 22px;
  font-size: var(--fs-xs);
  padding: 0 5px;
  gap: 4px;
}
.umv:not(.umv--mobile) .umv__chip:hover { filter: brightness(0.94); }
.umv:not(.umv--mobile) .umv__chip :deep(.mini-av) { width: 16px; height: 16px; }
.umv:not(.umv--mobile) .umv__chip :deep(.mini-av--txt) { font-size: var(--fs-3xs); }

@media (max-width: 340px) {
  .umv__holiday { display: none; }
}

/* ---------- 드래그로 날짜 옮기기 ---------- */

/* 잡을 수 있는 칩만 손 모양이 된다 — 권한 없는 항목은 잡히지 않는다는 신호 */
.umv:not(.umv--mobile) .umv__chip.is-draggable { cursor: grab; }
/*
 * 끌고 있는 동안은 그리드 어디에 있든 grabbing.
 * 특이도를 위 규칙(0,4,0)과 맞춰야 한다 — `.umv.is-dragging .umv__chip`(0,3,0)
 * 만으로는 grab 을 이기지 못해 끄는 내내 커서가 안 바뀌었다.
 * body 커서로도 안 된다. 칩/칸이 각자 cursor 를 갖고 있어서 그쪽이 이긴다.
 */
.umv.is-dragging .umv__chip.is-draggable,
.umv.is-dragging .umv__day,
.umv.is-dragging .umv__more { cursor: grabbing; }
/* 끌고 있는 동안의 호버 밝기 변화는 방해만 된다 */
.umv.is-dragging .umv__chip:hover { filter: none; }

/*
 * 놓일 자리의 플레이스홀더는 **옮겨진 칩 자신**이다(별도 상자를 그리지 않는다).
 * 아직 확정이 아니라는 것만 표시한다:
 *   - 안쪽 점선을 currentColor 로 — 채운 일정 칩(흰 글자)과 옅은 업무 칩
 *     (어두운 글자) 양쪽에서 보인다. 한 색을 박으면 둘 중 하나에서 사라진다.
 *   - 바깥 이중 링(표면색 1px + 강조색 2px) — 유형색이 무엇이든 배경과
 *     분리되어 읽힌다. box-shadow 라 레이아웃을 밀지 않는다.
 */
/* 끌고 있는 칩은 같은 칸의 다른 칩(2)과 날짜 버튼(1) 위에 있어야 한다.
   그리드 안에서만 겨루는 값이라 --z-* 전역 스케일에 넣지 않는다. */
.umv__chip.is-moving {
  z-index: 5;
  outline: 1px dashed currentColor;
  outline-offset: -3px;
  box-shadow: 0 0 0 1px var(--color-surface), 0 0 0 3px var(--color-primary), var(--sh-2);
  /* 착지 칸은 기하로 계산하므로 이 칩이 포인터를 받을 이유가 없다 */
  pointer-events: none;
}

/* 그리드 밖 — 지금 놓으면 아무 일도 일어나지 않는다 */
.umv__chip.is-moving.is-outside {
  outline: none;
  box-shadow: none;
  opacity: 0.35;
}

/* 덮게 될 칸 전체. .is-other-month 와 특이도가 같으므로 뒤에 와야 이긴다. */
.umv__day.is-drop-target { background: var(--color-primary-soft); }

.umv__droppill {
  position: fixed;
  /* 위치는 transform 으로만 준다(watchEffect 가 쓴다) — 합성만으로 움직인다 */
  left: 0;
  top: 0;
  z-index: var(--z-dialog);
  pointer-events: none;
  max-width: 40vw;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 3px var(--sp-2);
  border-radius: var(--r-full);
  background: var(--color-text);
  color: var(--color-on-accent);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: 1.4;
  white-space: nowrap;
  box-shadow: var(--sh-2);
}
</style>
